/**
 * Subject-side status tile for upward feedback campaigns.
 *
 * Phase 1.11 of the Upward Feedback Campaigns project. Shown on the
 * In-the-Moment Feedback tab to managers / CGMs who are the SUBJECT of
 * one-or-more active campaigns. Surfaces aggregated counts only — never
 * any respondent identity or response content (anonymity is the point).
 */

import { useEffect, useState } from 'react'
import { Megaphone, Loader2, ShieldCheck } from 'lucide-react'
import { parseApiDate } from '@/lib/apiDate'
import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import feedbackCampaignsService, {
  type UserCampaignSubject,
} from '@/services/api/feedbackCampaignsService'

interface Props {
  userId: string
}

export default function CampaignSubjectStatusTile({ userId }: Props) {
  const [items, setItems] = useState<UserCampaignSubject[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!userId) return
    let cancelled = false
    setLoading(true)
    feedbackCampaignsService
      .getUserCampaignSubjects(userId)
      .then((data) => {
        if (cancelled) return
        // Hide cancelled campaigns; otherwise show everything (open + closed)
        // so a manager still sees historical participation after close.
        setItems(data.filter((d) => d.status !== 'cancelled'))
      })
      .catch((err) => {
        if (cancelled) return
        // Quiet failure — the tile just doesn't render. Don't disrupt the
        // rest of the feedback page if the endpoint is unreachable (e.g.
        // RBAC denies, or no campaigns exist for this user).
        setError(err?.response?.data?.detail || 'unavailable')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [userId])

  if (loading) {
    return (
      <Card className="mb-6 border-purple-200 bg-purple-50/30">
        <CardContent className="py-4 flex items-center gap-3 text-sm text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking for upward feedback campaigns…
        </CardContent>
      </Card>
    )
  }

  if (error || items.length === 0) return null

  return (
    <Card className="mb-6 border-purple-200 bg-purple-50/30">
      <CardContent className="py-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-purple-100 p-2">
            <Megaphone className="h-5 w-5 text-purple-700" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-slate-900">
                Upward feedback about you
              </h3>
              <Badge variant="secondary" className="bg-white/80 text-purple-700">
                <ShieldCheck className="h-3 w-3 mr-1" />
                Confidential
              </Badge>
            </div>
            <p className="text-sm text-slate-600 mt-1">
              {items.length === 1
                ? 'Your team is contributing to a campaign about you.'
                : `Your team is contributing to ${items.length} campaigns about you.`}{' '}
              Responses are confidential and not attributed to individuals — HR will
              share a consolidated report with your one-up people leader for discussion
              during your mid-year dialogue.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {items.map((it) => {
            const pct = it.recipient_count
              ? Math.round((it.response_count / it.recipient_count) * 100)
              : 0
            // parseApiDate, not new Date(): campaign close dates come back
            // offset-less and are UTC by convention — see @/lib/apiDate.
            const closeText = it.close_at
              ? parseApiDate(it.close_at).toLocaleDateString(undefined, {
                  dateStyle: 'medium',
                })
              : '—'
            return (
              <div
                key={it.campaign_id}
                className="rounded-lg border border-purple-100 bg-white p-3"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-medium text-slate-900">{it.name}</div>
                  <Badge
                    className={
                      it.status === 'launched'
                        ? 'bg-blue-100 text-blue-700'
                        : it.status === 'closed'
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-slate-100 text-slate-600'
                    }
                  >
                    {it.status}
                  </Badge>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Closes {closeText} ·{' '}
                  <span className="tabular-nums">
                    {it.response_count} of {it.recipient_count}
                  </span>{' '}
                  responded ({pct}%)
                </div>
                <Progress value={pct} className="mt-2 h-1.5" />
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
